import { useMemo } from "react";
import { formatEUR0, formatNumber, formatPct0, normalizePercentValue } from "@/lib/pilotageUi";
import { BarChart3, AlertCircle, Pencil, Info } from "lucide-react";

const getRobustnessStatus = (robustness) => {
  if (robustness === null) return { label: "N/A", colorClass: "text-slate-400 border-white/10 bg-white/[0.03]" };
  if (robustness >= 4.0) return { label: "Sécurisé", colorClass: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" };
  if (robustness >= 3.0) return { label: "Vigilance", colorClass: "text-amber-400 border-amber-500/30 bg-amber-500/10" };
  return { label: "Risque", colorClass: "text-rose-400 border-rose-500/30 bg-rose-500/10" };
};

/**
 * Statut de l'écart Résultat réel vs cible
 * - Conforme  : réel >= cible
 * - Vigilance : réel entre 80% et 100% de la cible
 * - Risque    : réel < 80% de la cible
 * - N/A       : cible absente
 */
const getResultStatus = (target, actual) => {
  if (target === null || actual === null) {
    return { label: "N/A", colorClass: "text-slate-400 border-white/10 bg-white/[0.03]", emoji: null };
  }
  // Handle case where target is 0 or negative (prudent scenario = 0)
  if (target <= 0) {
    if (actual >= target) {
      return { label: "Conforme", colorClass: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", emoji: "🟢" };
    }
    return { label: "Risque", colorClass: "text-rose-400 border-rose-500/30 bg-rose-500/10", emoji: "🔴" };
  }
  if (actual >= target) {
    return { label: "Conforme", colorClass: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10", emoji: "🟢" };
  }
  if (actual >= target * 0.8) {
    return { label: "Vigilance", colorClass: "text-amber-400 border-amber-500/30 bg-amber-500/10", emoji: "🟠" };
  }
  return { label: "Risque", colorClass: "text-rose-400 border-rose-500/30 bg-rose-500/10", emoji: "🔴" };
};

const ChargesFixesRentabilite = ({
  isCeoView = false,
  selectedObjective = null,
  objectives = [],
  cockpitPayload = null,
  loading = false,
  onEditTarget = null,
}) => {
  const fixedCostsActual = cockpitPayload?.finance?.fixed_costs_annual_total ?? 0;
  const fixedCostsByCategory = cockpitPayload?.charts?.fixed_costs_monthly_by_category ?? [];
  // Résultat net réel annuel depuis le cockpit (finance.net_profit_annual)
  const netProfitActual = cockpitPayload?.finance?.net_profit_annual ?? null;
  const margeReelle = cockpitPayload?.finance?.average_margin_pct ?? null;

  // 1. Compute selected objective targets & real costs metrics
  const metrics = useMemo(() => {
    if (!selectedObjective) return null;

    const planned = selectedObjective.targets?.fixed_costs_target ?? null;
    const marginTarget = selectedObjective.targets?.margin_target ?? null;
    const forecastResultTarget = selectedObjective.targets?.forecast_result_target ?? null;

    const margeMissions = cockpitPayload?.finance?.mission_profit_annual ?? null;

    const delta = planned !== null ? fixedCostsActual - planned : null;

    const couverture = (margeMissions !== null && fixedCostsActual > 0)
      ? margeMissions / fixedCostsActual
      : null;

    const robustnesseStatus = getRobustnessStatus(couverture);

    // Écart Résultat prévisionnel cible vs réel
    const resultDelta = (forecastResultTarget !== null && netProfitActual !== null)
      ? netProfitActual - forecastResultTarget
      : null;
    const resultStatus = getResultStatus(forecastResultTarget, netProfitActual);

    return {
      planned,
      actual: fixedCostsActual,
      delta,
      couverture,
      robustnesseStatus,
      forecastResultTarget,
      netProfitActual,
      resultDelta,
      resultStatus,
      marginTarget,
    };
  }, [selectedObjective, fixedCostsActual, netProfitActual, cockpitPayload]);

  // 2. Retrieve comparisons side-by-side
  const planComparisons = useMemo(() => {
    const plans = {
      prudent: objectives.find(o => o.slug === "prudent" || o.slug?.includes("prudent")),
      realiste: objectives.find(o => o.slug === "realiste" || o.slug?.includes("realiste") || o.slug?.includes("board")),
      optimiste: objectives.find(o => o.slug === "optimiste" || o.slug?.includes("optimiste")),
    };

    const extractDetails = (obj) => {
      if (!obj) return null;
      const revenue = obj.targets?.annual_revenue_target ?? null;
      const margin = obj.targets?.margin_target ?? null;
      const planned = obj.targets?.fixed_costs_target ?? null;
      const etp = obj.targets?.total_annual_billed_etp ?? null;
      const forecastResultTarget = obj.targets?.forecast_result_target ?? null;

      const marge = (revenue !== null && margin !== null) ? revenue * margin : null;
      const res = (marge !== null) ? marge - fixedCostsActual : null;
      const robustness = (marge !== null && fixedCostsActual > 0) ? marge / fixedCostsActual : null;

      // Écart entre la cible et le réel actuel
      const resultDelta = (forecastResultTarget !== null && netProfitActual !== null)
        ? netProfitActual - forecastResultTarget
        : null;

      return {
        name: obj.name,
        revenue,
        margin,
        marge,
        planned,
        actual: fixedCostsActual,
        res,
        etp,
        robustness,
        forecastResultTarget,
        resultDelta,
      };
    };

    return {
      prudent: extractDetails(plans.prudent),
      realiste: extractDetails(plans.realiste),
      optimiste: extractDetails(plans.optimiste),
    };
  }, [objectives, fixedCostsActual, netProfitActual]);

  const activeSlug = selectedObjective?.slug?.toLowerCase() || "";
  const isPrudentActive = activeSlug.includes("prudent");
  const isRealisteActive = activeSlug.includes("realiste") || activeSlug.includes("board");
  const isOptimisteActive = activeSlug.includes("optimiste");

  const getHeaderClass = (isActive) => {
    return `py-2.5 px-4 text-right font-semibold transition-all duration-200 ${
      isActive
        ? "text-lenvia-blue bg-lenvia-blue/10 border-x border-t border-lenvia-blue/20"
        : "text-slate-400"
    }`;
  };

  const getCellClass = (isActive, extraClasses = "") => {
    return `py-3 px-4 text-right font-mono transition-all duration-200 ${extraClasses} ${
      isActive
        ? "bg-lenvia-blue/[0.04] border-x border-lenvia-blue/10 font-semibold"
        : ""
    }`;
  };

  // planned - actual: positive is economy (favorable)
  const fixedCostsDelta = (metrics?.planned !== null && metrics?.planned !== undefined)
    ? metrics.planned - fixedCostsActual
    : null;

  const marginDelta = (margeReelle !== null && metrics?.marginTarget !== null && metrics?.marginTarget !== undefined)
    ? (normalizePercentValue(margeReelle) || 0) - (normalizePercentValue(metrics.marginTarget) || 0)
    : null;

  if (loading) {
    return (
      <div className="rounded-3xl border border-white/10 glass p-6">
        <p className="text-sm text-slate-300">Chargement de l'analyse des charges fixes & rentabilité...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="charges-fixes-rentabilite-section">
      {/* SECTION HEADER */}
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-lenvia-blue/10 p-2.5 text-lenvia-blue border border-lenvia-blue/20">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Charges & Rentabilité</p>
            <h2 className="text-xl font-heading font-semibold text-white">Charges fixes & Rentabilité</h2>
          </div>
        </div>

        {/* KPI CARDS STRUCTURE */}
        {metrics && (
          <div className="mt-8 space-y-8">
            {/* BLOC 1 — BUSINESS PLAN */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-500"></span>
                BLOC 1 — BUSINESS PLAN
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <article className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 relative group hover:border-white/10 transition-colors">
                  <div className="flex justify-between items-start">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Charges fixes prévues</p>
                    {onEditTarget && (
                      <button
                        type="button"
                        onClick={() => onEditTarget("fixed_costs_target")}
                        className="rounded-lg p-1.5 text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                        title="Modifier cette cible"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="mt-3 text-2xl font-bold text-white font-sans">
                    {metrics.planned !== null ? formatEUR0(metrics.planned) : "—"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Budget annuel du plan cible</p>
                </article>

                <article className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 relative group hover:border-white/10 transition-colors">
                  <div className="flex justify-between items-start">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Résultat cible</p>
                    {onEditTarget && (
                      <button
                        type="button"
                        onClick={() => onEditTarget("forecast_result_target")}
                        className="rounded-lg p-1.5 text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                        title="Modifier la cible Résultat prévisionnel"
                        data-testid="edit-forecast-result-target-btn"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className={`mt-3 text-2xl font-bold font-sans ${metrics.forecastResultTarget !== null ? (metrics.forecastResultTarget > 0 ? "text-emerald-400" : metrics.forecastResultTarget === 0 ? "text-slate-300" : "text-rose-400") : "text-white"}`}
                     data-testid="forecast-result-target-value">
                    {metrics.forecastResultTarget !== null ? formatEUR0(metrics.forecastResultTarget) : "—"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Objectif annuel prévisionnel</p>
                </article>

                <article className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 relative hover:border-white/10 transition-colors">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Marge cible</p>
                  <p className="mt-3 text-2xl font-bold text-white font-sans">
                    {metrics.marginTarget !== null ? formatPct0(metrics.marginTarget) : "—"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Objectif de marge du plan cible</p>
                </article>
              </div>
            </div>

            {/* BLOC 2 — RÉALISÉ */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-lenvia-blue mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-lenvia-blue animate-pulse"></span>
                BLOC 2 — RÉALISÉ
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <article className="rounded-2xl border border-lenvia-blue/20 bg-lenvia-blue/[0.02] p-4 relative group hover:border-lenvia-blue/30 transition-colors">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-300">Charges fixes réelles</p>
                  <p className="mt-3 text-2xl font-bold text-white font-sans">
                    {formatEUR0(metrics.actual)}
                  </p>
                  <p className="mt-1 text-xs text-blue-200/60">
                    vs prévu : {metrics.planned !== null ? formatEUR0(metrics.planned) : "—"}
                  </p>
                  {fixedCostsDelta !== null && (
                    <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${fixedCostsDelta >= 0 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-rose-400 border-rose-500/30 bg-rose-500/10"}`}>
                      {fixedCostsDelta >= 0 ? "🟢" : "🔴"} {fixedCostsDelta >= 0 ? `+${formatEUR0(fixedCostsDelta)} (économie)` : `-${formatEUR0(Math.abs(fixedCostsDelta))} (dépassement)`}
                    </div>
                  )}
                </article>

                <article className="rounded-2xl border border-lenvia-blue/20 bg-lenvia-blue/[0.02] p-4 relative group hover:border-lenvia-blue/30 transition-colors"
                         title={`Couverture des charges : ${metrics.couverture !== null ? `${metrics.couverture.toFixed(1)}x` : "—"} (Marge / Charges fixes)`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-300 flex items-center gap-1.5">
                    Résultat réel
                    <Info className="h-3.5 w-3.5 text-blue-400/60 cursor-help" />
                  </p>
                  <p className={`mt-3 text-2xl font-bold font-sans ${metrics.netProfitActual !== null ? (metrics.netProfitActual >= 0 ? "text-emerald-400" : "text-rose-400") : "text-white"}`}
                     data-testid="net-profit-actual-value">
                    {metrics.netProfitActual !== null ? (metrics.netProfitActual >= 0 ? `+${formatEUR0(metrics.netProfitActual)}` : formatEUR0(metrics.netProfitActual)) : "—"}
                  </p>
                  <p className="mt-1 text-xs text-blue-200/60">
                    vs objectif : {metrics.forecastResultTarget !== null ? formatEUR0(metrics.forecastResultTarget) : "—"}
                  </p>
                  {metrics.resultDelta !== null && (
                    <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${metrics.resultDelta >= 0 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-rose-400 border-rose-500/30 bg-rose-500/10"}`}>
                      {metrics.resultDelta >= 0 ? "🟢" : "🔴"} {metrics.resultDelta >= 0 ? `+${formatEUR0(metrics.resultDelta)}` : `${formatEUR0(metrics.resultDelta)}`}
                    </div>
                  )}
                </article>

                <article className="rounded-2xl border border-lenvia-blue/20 bg-lenvia-blue/[0.02] p-4 relative group hover:border-lenvia-blue/30 transition-colors">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-300">Marge réelle</p>
                  <p className="mt-3 text-2xl font-bold text-white font-sans">
                    {margeReelle !== null ? formatPct0(margeReelle) : "—"}
                  </p>
                  <p className="mt-1 text-xs text-blue-200/60">
                    vs cible : {metrics.marginTarget !== null ? formatPct0(metrics.marginTarget) : "—"}
                  </p>
                  {marginDelta !== null && (
                    <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${marginDelta >= 0 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-rose-400 border-rose-500/30 bg-rose-500/10"}`}>
                      {marginDelta >= 0 ? "🟢" : "🔴"} {marginDelta >= 0 ? `+${marginDelta.toFixed(1)} %` : `${marginDelta.toFixed(1)} %`}
                    </div>
                  )}
                </article>
              </div>
            </div>

            {/* BLOC 3 — PERFORMANCE */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-400 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                BLOC 3 — PERFORMANCE
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 relative group hover:border-white/20 transition-colors">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Écart charges</p>
                  <p className={`mt-3 text-2xl font-bold font-sans ${fixedCostsDelta !== null ? (fixedCostsDelta >= 0 ? "text-emerald-400" : "text-rose-400") : "text-white"}`}>
                    {fixedCostsDelta !== null ? (fixedCostsDelta >= 0 ? `+${formatEUR0(fixedCostsDelta)}` : formatEUR0(fixedCostsDelta)) : "—"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {fixedCostsDelta !== null ? (fixedCostsDelta >= 0 ? "Économie réalisée" : "Dépassement de budget") : "—"}
                  </p>
                  {fixedCostsDelta !== null && (
                    <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${fixedCostsDelta >= 0 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-rose-400 border-rose-500/30 bg-rose-500/10"}`}>
                      {fixedCostsDelta >= 0 ? "🟢 Conforme" : "🔴 Risque"}
                    </div>
                  )}
                </article>

                <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 relative group hover:border-white/20 transition-colors">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Écart résultat</p>
                  <p className={`mt-3 text-2xl font-bold font-sans ${metrics.resultDelta !== null ? (metrics.resultDelta >= 0 ? "text-emerald-400" : "text-rose-400") : "text-slate-300"}`}
                     data-testid="result-delta-value">
                    {metrics.resultDelta !== null ? (metrics.resultDelta >= 0 ? `+${formatEUR0(metrics.resultDelta)}` : formatEUR0(metrics.resultDelta)) : "—"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Réel vs cible du plan</p>
                  {metrics.resultDelta !== null && (
                    <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${metrics.resultDelta >= 0 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-rose-400 border-rose-500/30 bg-rose-500/10"}`}
                         data-testid="result-status-badge">
                      {metrics.resultDelta >= 0 ? "🟢 Conforme" : "🔴 Risque"}
                    </div>
                  )}
                </article>

                <article className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 relative group hover:border-white/20 transition-colors">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Statut global</p>
                  <p className={`mt-3 text-2xl font-bold font-sans ${metrics.resultStatus.colorClass.includes("emerald") ? "text-emerald-400" : metrics.resultStatus.colorClass.includes("amber") ? "text-amber-400" : "text-rose-400"}`}>
                    {metrics.resultStatus.label === "Conforme" ? "Conforme" : metrics.resultStatus.label === "Vigilance" ? "Attention" : metrics.resultStatus.label === "Risque" ? "Risque" : "N/A"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Basé sur l'écart de résultat</p>
                  {metrics.resultStatus.emoji && (
                    <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${metrics.resultStatus.colorClass}`}>
                      {metrics.resultStatus.emoji} {metrics.resultStatus.label}
                    </div>
                  )}
                </article>
              </div>
            </div>
          </div>
        )}

        {/* 3. TABLEAU COMPARATIF BUSINESS PLAN */}
        <div className="mt-8 border-t border-white/10 pt-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Tableau comparatif Business Plan</p>
          <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-slate-400 uppercase tracking-wider bg-slate-900/40">
                  <th className="py-2.5 px-4 font-semibold sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">KPI</th>
                  <th className={getHeaderClass(isPrudentActive)}>Prudent</th>
                  <th className={getHeaderClass(isRealisteActive)}>Réaliste</th>
                  <th className={getHeaderClass(isOptimisteActive)}>Optimiste</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-slate-200">
                <tr>
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">CA annuel cible</td>
                  <td className={getCellClass(isPrudentActive)}>
                    {planComparisons.prudent?.revenue != null ? formatEUR0(planComparisons.prudent.revenue) : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive)}>
                    {planComparisons.realiste?.revenue != null ? formatEUR0(planComparisons.realiste.revenue) : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive)}>
                    {planComparisons.optimiste?.revenue != null ? formatEUR0(planComparisons.optimiste.revenue) : "—"}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Marge cible</td>
                  <td className={getCellClass(isPrudentActive)}>
                    {planComparisons.prudent?.margin != null ? formatPct0(planComparisons.prudent.margin) : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive)}>
                    {planComparisons.realiste?.margin != null ? formatPct0(planComparisons.realiste.margin) : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive)}>
                    {planComparisons.optimiste?.margin != null ? formatPct0(planComparisons.optimiste.margin) : "—"}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Charges fixes prévues</td>
                  <td className={getCellClass(isPrudentActive)}>
                    {planComparisons.prudent?.planned != null ? formatEUR0(planComparisons.prudent.planned) : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive)}>
                    {planComparisons.realiste?.planned != null ? formatEUR0(planComparisons.realiste.planned) : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive)}>
                    {planComparisons.optimiste?.planned != null ? formatEUR0(planComparisons.optimiste.planned) : "—"}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Charges fixes réelles</td>
                  <td className={getCellClass(isPrudentActive)}>
                    {formatEUR0(fixedCostsActual)}
                  </td>
                  <td className={getCellClass(isRealisteActive)}>
                    {formatEUR0(fixedCostsActual)}
                  </td>
                  <td className={getCellClass(isOptimisteActive)}>
                    {formatEUR0(fixedCostsActual)}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Écart charges</td>
                  <td className={getCellClass(isPrudentActive, planComparisons.prudent?.planned != null ? (planComparisons.prudent.planned - fixedCostsActual >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {planComparisons.prudent?.planned != null ? (planComparisons.prudent.planned - fixedCostsActual >= 0 ? `+${formatEUR0(planComparisons.prudent.planned - fixedCostsActual)}` : formatEUR0(planComparisons.prudent.planned - fixedCostsActual)) : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive, planComparisons.realiste?.planned != null ? (planComparisons.realiste.planned - fixedCostsActual >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {planComparisons.realiste?.planned != null ? (planComparisons.realiste.planned - fixedCostsActual >= 0 ? `+${formatEUR0(planComparisons.realiste.planned - fixedCostsActual)}` : formatEUR0(planComparisons.realiste.planned - fixedCostsActual)) : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive, planComparisons.optimiste?.planned != null ? (planComparisons.optimiste.planned - fixedCostsActual >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {planComparisons.optimiste?.planned != null ? (planComparisons.optimiste.planned - fixedCostsActual >= 0 ? `+${formatEUR0(planComparisons.optimiste.planned - fixedCostsActual)}` : formatEUR0(planComparisons.optimiste.planned - fixedCostsActual)) : "—"}
                  </td>
                </tr>
                <tr data-testid="comparison-forecast-result-target-row">
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Résultat prévisionnel cible</td>
                  <td className={getCellClass(isPrudentActive, planComparisons.prudent?.forecastResultTarget != null ? (planComparisons.prudent.forecastResultTarget > 0 ? "text-emerald-400" : planComparisons.prudent.forecastResultTarget === 0 ? "text-slate-400" : "text-rose-400") : "")}>
                    {planComparisons.prudent?.forecastResultTarget != null ? (planComparisons.prudent.forecastResultTarget > 0 ? `+${formatEUR0(planComparisons.prudent.forecastResultTarget)}` : formatEUR0(planComparisons.prudent.forecastResultTarget)) : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive, planComparisons.realiste?.forecastResultTarget != null ? (planComparisons.realiste.forecastResultTarget > 0 ? "text-emerald-400" : planComparisons.realiste.forecastResultTarget === 0 ? "text-slate-400" : "text-rose-400") : "")}>
                    {planComparisons.realiste?.forecastResultTarget != null ? (planComparisons.realiste.forecastResultTarget > 0 ? `+${formatEUR0(planComparisons.realiste.forecastResultTarget)}` : formatEUR0(planComparisons.realiste.forecastResultTarget)) : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive, planComparisons.optimiste?.forecastResultTarget != null ? (planComparisons.optimiste.forecastResultTarget > 0 ? "text-emerald-400" : planComparisons.optimiste.forecastResultTarget === 0 ? "text-slate-400" : "text-rose-400") : "")}>
                    {planComparisons.optimiste?.forecastResultTarget != null ? (planComparisons.optimiste.forecastResultTarget > 0 ? `+${formatEUR0(planComparisons.optimiste.forecastResultTarget)}` : formatEUR0(planComparisons.optimiste.forecastResultTarget)) : "—"}
                  </td>
                </tr>
                <tr data-testid="comparison-net-profit-actual-row">
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Résultat réel actuel</td>
                  <td className={getCellClass(isPrudentActive, netProfitActual !== null ? (netProfitActual >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {netProfitActual !== null ? (netProfitActual >= 0 ? `+${formatEUR0(netProfitActual)}` : formatEUR0(netProfitActual)) : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive, netProfitActual !== null ? (netProfitActual >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {netProfitActual !== null ? (netProfitActual >= 0 ? `+${formatEUR0(netProfitActual)}` : formatEUR0(netProfitActual)) : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive, netProfitActual !== null ? (netProfitActual >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {netProfitActual !== null ? (netProfitActual >= 0 ? `+${formatEUR0(netProfitActual)}` : formatEUR0(netProfitActual)) : "—"}
                  </td>
                </tr>
                <tr data-testid="comparison-result-delta-row">
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Écart résultat</td>
                  <td className={getCellClass(isPrudentActive, planComparisons.prudent?.resultDelta != null ? (planComparisons.prudent.resultDelta >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {planComparisons.prudent?.resultDelta != null
                      ? (planComparisons.prudent.resultDelta >= 0 ? `+${formatEUR0(planComparisons.prudent.resultDelta)}` : formatEUR0(planComparisons.prudent.resultDelta))
                      : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive, planComparisons.realiste?.resultDelta != null ? (planComparisons.realiste.resultDelta >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {planComparisons.realiste?.resultDelta != null
                      ? (planComparisons.realiste.resultDelta >= 0 ? `+${formatEUR0(planComparisons.realiste.resultDelta)}` : formatEUR0(planComparisons.realiste.resultDelta))
                      : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive, planComparisons.optimiste?.resultDelta != null ? (planComparisons.optimiste.resultDelta >= 0 ? "text-emerald-400" : "text-rose-400") : "")}>
                    {planComparisons.optimiste?.resultDelta != null
                      ? (planComparisons.optimiste.resultDelta >= 0 ? `+${formatEUR0(planComparisons.optimiste.resultDelta)}` : formatEUR0(planComparisons.optimiste.resultDelta))
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">Couverture des charges</td>
                  <td className={getCellClass(isPrudentActive)}>
                    {planComparisons.prudent?.robustness != null ? `${planComparisons.prudent.robustness.toFixed(1)}x` : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive)}>
                    {planComparisons.realiste?.robustness != null ? `${planComparisons.realiste.robustness.toFixed(1)}x` : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive)}>
                    {planComparisons.optimiste?.robustness != null ? `${planComparisons.optimiste.robustness.toFixed(1)}x` : "—"}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-slate-400 sticky left-0 bg-[#0d1222] z-10 border-r border-white/5 whitespace-nowrap">ETP facturés cible</td>
                  <td className={getCellClass(isPrudentActive)}>
                    {planComparisons.prudent?.etp != null ? formatNumber(planComparisons.prudent.etp, { maximumFractionDigits: 1 }) : "—"}
                  </td>
                  <td className={getCellClass(isRealisteActive)}>
                    {planComparisons.realiste?.etp != null ? formatNumber(planComparisons.realiste.etp, { maximumFractionDigits: 1 }) : "—"}
                  </td>
                  <td className={getCellClass(isOptimisteActive)}>
                    {planComparisons.optimiste?.etp != null ? formatNumber(planComparisons.optimiste.etp, { maximumFractionDigits: 1 }) : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 4. DETAILED VIEW ONLY */}
      {!isCeoView && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* DETAILED FIXED COSTS BREAKDOWN */}
          <section className="rounded-3xl border border-white/10 glass-heavy p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Détail du cockpit</p>
            <h3 className="mt-2 text-lg font-heading font-semibold text-white">Répartition des charges fixes réelles</h3>
            <div className="mt-6 space-y-4">
              {fixedCostsByCategory.length > 0 ? (
                fixedCostsByCategory.slice(0, 8).map((cat, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm border-b border-white/5 pb-2">
                    <span className="text-slate-300 font-medium">{cat.label}</span>
                    <span className="text-white font-semibold">{formatEUR0(cat.amount_monthly_total * 12)} <span className="text-[10px] text-slate-400">/ an</span></span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400">Aucune charge fixe réelle active trouvée pour cette année.</p>
              )}
            </div>
          </section>

          {/* DEVIATION CALCULATIONS & SUMMARY */}
          <section className="rounded-3xl border border-white/10 glass-heavy p-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Calcul complet</p>
            <h3 className="mt-2 text-lg font-heading font-semibold text-white">Calcul complet des écarts</h3>
            <div className="mt-6 space-y-3 text-sm text-slate-300">
              <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4 space-y-2">
                <p className="flex justify-between">
                  <span>Charges fixes réelles (R)</span>
                  <span className="text-white font-semibold">{formatEUR0(fixedCostsActual)}</span>
                </p>
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <span>Charges fixes prévues (P)</span>
                  <span className="text-white font-semibold">{metrics?.planned !== null ? formatEUR0(metrics.planned) : "—"}</span>
                </p>
                <p className="flex justify-between pt-1">
                  <span className="font-semibold text-slate-400">Écart absolu (R - P)</span>
                  <span className={`font-semibold ${metrics?.delta > 0 ? "text-rose-400" : metrics?.delta < 0 ? "text-emerald-400" : "text-white"}`}>
                    {metrics?.delta !== null ? (metrics.delta >= 0 ? `+${formatEUR0(metrics.delta)}` : formatEUR0(metrics.delta)) : "—"}
                  </span>
                </p>
                <p className="flex justify-between">
                  <span className="font-semibold text-slate-400">Écart relatif ((R - P) / P)</span>
                  {(() => {
                    const deltaPct = (metrics?.planned !== null && metrics?.planned > 0) ? (metrics.delta / metrics.planned) * 100 : null;
                    return (
                      <span className={`font-semibold ${deltaPct > 0 ? "text-rose-400" : deltaPct < 0 ? "text-emerald-400" : "text-white"}`}>
                        {deltaPct !== null ? (deltaPct >= 0 ? `+${deltaPct.toFixed(1)}%` : `${deltaPct.toFixed(1)}%`) : "—"}
                      </span>
                    );
                  })()}
                </p>
              </div>

              <div className="flex gap-2.5 text-xs text-slate-400 bg-lenvia-blue/5 border border-lenvia-blue/10 rounded-xl p-3.5 mt-4">
                <AlertCircle className="h-4 w-4 text-lenvia-blue shrink-0 mt-0.5" />
                <p>
                  Un écart positif indique un dépassement des charges réelles par rapport au plan, tandis qu'un écart négatif représente une économie réalisée.
                </p>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default ChargesFixesRentabilite;
